import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/utils/api';

const Login = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'HR',
    registration_key: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await api.post('/auth/login', {
          email: formData.email,
          password: formData.password,
        });
        toast.success('Login successful!');
        onLogin(response.data.access_token, response.data.user);
      } else {
        await api.post('/auth/register', formData);
        toast.success('Registration successful! Please login.');
        setIsLogin(true);
        setFormData({ ...formData, password: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div>
            <img src="/logo.png" alt="Mr. Chow's" className="h-16 mb-4" data-testid="login-logo" />
            <h1 className="text-4xl font-bold tracking-tight text-slate-900">MPS</h1>
            <p className="mt-2 text-lg text-slate-600">Welcome to Mr. Chow's Payroll System (MPS)</p>
          </div>

          <div className="mt-8">
            <div className="flex gap-4 mb-6">
              <Button
                type="button"
                onClick={() => setIsLogin(true)}
                variant={isLogin ? 'default' : 'outline'}
                className="flex-1"
                data-testid="login-tab-button"
              >
                Login
              </Button>
              <Button
                type="button"
                onClick={() => setIsLogin(false)}
                variant={!isLogin ? 'default' : 'outline'}
                className="flex-1"
                data-testid="register-tab-button"
              >
                Register
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                />
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="role">Role</Label>
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                    data-testid="role-select"
                  >
                    <option value="HR">HR</option>
                    <option value="Manager">Manager</option>
                    <option value="CEO">CEO</option>
                    <option value="Employee">Employee</option>
                  </select>
                </div>
              )}

              {!isLogin && (
                <div>
                  <Label htmlFor="registration_key">Registration Key</Label>
                  <Input
                    id="registration_key"
                    type="password"
                    value={formData.registration_key}
                    onChange={(e) => setFormData({ ...formData, registration_key: e.target.value })}
                    required={!isLogin}
                    placeholder="Enter registration key"
                    data-testid="registration-key-input"
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? 'Please wait...' : isLogin ? 'Login' : 'Register'}
              </Button>
            </form>

            {isLogin && (
              <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs font-medium text-slate-600 mb-2">Demo Credentials:</p>
                <p className="text-xs text-slate-500">Create an account to get started</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right side - Logo */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900 items-center justify-center" data-testid="login-right-panel">
        <div className="flex flex-col items-center space-y-8 p-12">
          <img
            src="/logo.png"
            alt="Mr. Chow's"
            className="w-72 rounded-2xl shadow-2xl"
            data-testid="login-hero-logo"
          />
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-bold tracking-tight text-white">Streamline Your Payroll</h2>
            <p className="text-base text-slate-400">Multi-level approval workflow with government compliance</p>
          </div>
          <div className="flex gap-4 mt-4">
            <div className="bg-white/10 backdrop-blur-md px-4 py-3 rounded-lg border border-white/20">
              <p className="text-sm text-slate-300">ESI Compliant</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md px-4 py-3 rounded-lg border border-white/20">
              <p className="text-sm text-slate-300">PF Management</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md px-4 py-3 rounded-lg border border-white/20">
              <p className="text-sm text-slate-300">PT Tracking</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
